import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { ErrorType } from '../../api/errorHandling';
import type {
	AttachMode,
	ColumnTemplate,
	DashboardTemplate,
} from '../../components/admin/widgetsGrids/types';
import type { ClientConfigurationItem } from '../clientConfiguration/clientConfigurationSlice';

export type ConfigUser = {
	Id: string;
	Name?: string | null;
	Surname?: string | null;
	Email?: string | null;
	Databases?: { Id: number; Client: string }[] | null;
};

export type AttachRequest = { userIds: string[]; mode: AttachMode } & (
	| { kind: 'dashboard'; template: DashboardTemplate }
	| { kind: 'columns'; template: ColumnTemplate }
);

export type AttachResult = {
	userId: string;
	status: 'pending' | 'done' | 'failed';
	error?: string;
};

export type UserConfigurationState = {
	/**
	 * null until a user read answered. false once a write came back filed
	 * under someone other than the target user (the backend scopes writes to
	 * the caller), or when the very first read got 404/405 (no list route).
	 * Once false, only a write confirmed under the target user sets it back:
	 * the scoped build answers every read of another user like "no rows".
	 */
	supported: boolean | null;
	users: ConfigUser[] | null;
	usersLoading: boolean;
	/** Each user's rows as last loaded or saved. */
	rows: Record<string, ClientConfigurationItem[]>;
	loadingUserId: string | null;
	loadError: string | null;
	saving: boolean;
	saveError: string | null;
	/** Counts successful saves, so editors can tell a save landed. */
	saveCount: number;
	attachRunning: boolean;
	attachResults: AttachResult[];
	/**
	 * The request behind `attachResults`, as it was when the run started
	 * (template payload, mode, page). Retry repeats this, never whatever
	 * template or mode the dialog shows now.
	 */
	attachRequest: AttachRequest | null;
};

const initialState: UserConfigurationState = {
	supported: null,
	users: null,
	usersLoading: false,
	rows: {},
	loadingUserId: null,
	loadError: null,
	saving: false,
	saveError: null,
	saveCount: 0,
	attachRunning: false,
	attachResults: [],
	attachRequest: null,
};

const mergeRow = (
	rows: ClientConfigurationItem[] | undefined,
	row: ClientConfigurationItem,
): ClientConfigurationItem[] => {
	const list = rows ?? [];
	const index = list.findIndex((r) => r.Id === row.Id);
	return index === -1
		? [...list, row]
		: list.map((r, i) => (i === index ? row : r));
};

const userConfigurationSlice = createSlice({
	name: 'userConfiguration',
	initialState,
	reducers: {
		loadConfigUsersStart(state) {
			state.usersLoading = true;
		},
		loadConfigUsersSuccess(
			state,
			action: PayloadAction<{ users: ConfigUser[] }>,
		) {
			state.users = action.payload.users;
			state.usersLoading = false;
		},
		loadConfigUsersFailure(state, _action: PayloadAction<ErrorType>) {
			state.users = state.users ?? [];
			state.usersLoading = false;
		},
		loadUserConfigStart(state, action: PayloadAction<{ userId: string }>) {
			state.loadingUserId = action.payload.userId;
			state.loadError = null;
		},
		loadUserConfigSuccess(
			state,
			action: PayloadAction<{
				userId: string;
				rows: ClientConfigurationItem[];
			}>,
		) {
			state.rows[action.payload.userId] = action.payload.rows;
			if (state.supported === null) state.supported = true;
			if (state.loadingUserId === action.payload.userId) {
				state.loadingUserId = null;
			}
		},
		loadUserConfigFailure(state, action: PayloadAction<ErrorType>) {
			state.loadingUserId = null;
			state.loadError = action.payload.message || 'error';
		},
		dropUserConfigRows(state, action: PayloadAction<{ userId: string }>) {
			delete state.rows[action.payload.userId];
		},
		markConfigUnsupported(state) {
			state.supported = false;
			state.loadingUserId = null;
			state.saving = false;
		},
		saveUserConfigStart(
			state,
			_action: PayloadAction<{
				userId: string;
				parameter: string;
				value: unknown;
			}>,
		) {
			state.saving = true;
			state.saveError = null;
		},
		saveUserConfigSuccess(
			state,
			action: PayloadAction<{
				userId: string;
				row: ClientConfigurationItem;
			}>,
		) {
			const { userId, row } = action.payload;
			state.rows[userId] = mergeRow(state.rows[userId], row);
			state.saving = false;
			state.supported = true;
			state.saveCount += 1;
		},
		saveUserConfigFailure(state, action: PayloadAction<ErrorType>) {
			state.saving = false;
			state.saveError = action.payload.message || 'error';
		},
		mergeUserConfigRow(
			state,
			action: PayloadAction<{ userId: string; row: ClientConfigurationItem }>,
		) {
			const { userId, row } = action.payload;
			state.rows[userId] = mergeRow(state.rows[userId], row);
		},
		attachTemplateStart(state, action: PayloadAction<AttachRequest>) {
			state.attachRunning = true;
			// Immer's draft type rejects the plain ColumnTemplate type; same value.
			state.attachRequest = action.payload as typeof state.attachRequest;
			state.attachResults = action.payload.userIds.map((userId) => ({
				userId,
				status: 'pending',
			}));
		},
		attachUserResult(state, action: PayloadAction<AttachResult>) {
			state.attachResults = state.attachResults.map((r) =>
				r.userId === action.payload.userId ? action.payload : r,
			);
		},
		attachTemplateFinish(state) {
			state.attachRunning = false;
			// Users skipped after the backend said "unsupported".
			state.attachResults = state.attachResults.map((r) =>
				r.status === 'pending' ? { ...r, status: 'failed' } : r,
			);
		},
	},
});

export const {
	loadConfigUsersStart,
	loadConfigUsersSuccess,
	loadConfigUsersFailure,
	loadUserConfigStart,
	loadUserConfigSuccess,
	loadUserConfigFailure,
	dropUserConfigRows,
	markConfigUnsupported,
	saveUserConfigStart,
	saveUserConfigSuccess,
	saveUserConfigFailure,
	mergeUserConfigRow,
	attachTemplateStart,
	attachUserResult,
	attachTemplateFinish,
} = userConfigurationSlice.actions;

export default userConfigurationSlice.reducer;
